import type { AgentFocus } from '@tunasong/agent-context'
import { features } from '@tunasong/redux'
import type { TunaThunkDispatch } from '@tunasong/redux'

interface OpenSidebarAgentWithFocusArgs {
  agentId: string
  focus: AgentFocus
  /** Optional draft. Pre-filled into the composer, or sent immediately when `autoSend`. */
  draft?: string
  /**
   * When true, the `draft` is sent immediately as an agent turn (one-click
   * contextual command) rather than pre-filled for the user to review and send.
   * The sidebar routes it to `Chat.autoSubmit`; the user lands on a streaming
   * response, and the agent's edit arrives as a reviewable proposal.
   */
  autoSend?: boolean
}

/**
 * Open the agent sidebar focused on a specific agent, with anchor context preset.
 * Fired by the block-affordance surfaces (the per-block menu `useBlockActions`
 * and the contextual palette commands) when a user picks an agent affordance.
 *
 * One-shot state: AgentChat reads `pendingFocus` and includes it on the outgoing
 * `agentStream`, then clears it. With `autoSend`, the draft is sent immediately;
 * without it, the draft pre-fills the composer for the user to review and send.
 */
export const openSidebarAgentWithFocus =
  (args: OpenSidebarAgentWithFocusArgs) =>
  (dispatch: TunaThunkDispatch) => {
    dispatch(features.ui.actions.setSidebarActiveTool('Agents'))
    dispatch(features.ui.actions.setSidebarActiveAgentId(args.agentId))
    dispatch(features.ui.actions.setSidebarPendingFocus(args.focus))
    if (args.draft !== undefined && args.draft.length > 0) {
      dispatch(features.ui.actions.setSidebarPendingDraft(args.draft))
    }
    dispatch(features.ui.actions.setSidebarPendingAutoSend(args.autoSend === true))
  }
